import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import MuiStepper, { StepperProps } from '@mui/material/Stepper';
import { Breakpoint, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import Box from '@mui/material/Box';

const stepperMaxWidths = {
  sm: 300,
  md: 450,
  lg: 600,
} as const;

type StepperMaxWidth = keyof typeof stepperMaxWidths;

const StyledStep = styled(Step, {
  shouldForwardProp: (prop) => prop !== 'isHidden' && prop !== 'noLeftPadding',
})<{ isHidden?: boolean; noLeftPadding?: boolean }>(
  ({ isHidden, noLeftPadding }) => ({
    display: isHidden ? 'none' : undefined,
    paddingLeft: noLeftPadding ? 0 : undefined,
  }),
);

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepIcon-root.Mui-active': {
    color: theme.palette.grey[900],
  },
}));

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'maxWidth',
})<{ maxWidth?: number }>(({ maxWidth }) => ({
  width: maxWidth,
  maxWidth,
  minWidth: 0,
}));

type Props = {
  steps: Array<string>;
  activeOnlyBreakpoint?: Breakpoint;
  maxWidth?: StepperMaxWidth;
} & Pick<StepperProps, 'orientation' | 'activeStep' | 'className'>;

export const Stepper: React.FC<Props> = ({
  steps,
  activeOnlyBreakpoint,
  maxWidth,
  activeStep,
  ...rest
}) => {
  const theme = useTheme();
  const belowBreakpoint = useMediaQuery(
    theme.breakpoints.down(activeOnlyBreakpoint ?? 'xs'),
  );
  const isCollapsed = !!activeOnlyBreakpoint && belowBreakpoint;

  const stepper = (
    <MuiStepper
      orientation="horizontal"
      activeStep={activeStep}
      connector={isCollapsed ? null : undefined}
      {...rest}
    >
      {steps.map((step, i) => (
        <StyledStep
          key={step}
          isHidden={isCollapsed ? i !== activeStep : false}
          noLeftPadding={i === 0 || (isCollapsed && i === activeStep)}
        >
          <StyledStepLabel>{step}</StyledStepLabel>
        </StyledStep>
      ))}
    </MuiStepper>
  );

  if (!maxWidth) return stepper;

  return <Wrapper maxWidth={stepperMaxWidths[maxWidth]}>{stepper}</Wrapper>;
};

export default Stepper;
